import { getQueryParameterFromUri } from "@WhiteCap/Common/Utillities/utils";
import {
    SubscribedEmailDto,
    SubscriptionsDashboardState,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import { ApiResultModel as AsnApiResultModel } from "@WhiteCap/Store/Service/OsnSubscriptionsServices/AsnSubscribersService";
import { Draft } from "immer";

const asnSubscriptions = {
    "Custom/SubscriptionDashboard/BeginAsnSubscribersDataLoad": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = true;
    },
    "Custom/SubscriptionDashboard/CompleteAsnSubscribersDataLoad": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: AsnApiResultModel;
        },
    ) => {
        const customerId = getQueryParameterFromUri(action.result?.uri, "customerId");
        const data = {
            properties: action.result.properties,
            subscribedEmails: action.result.subscribedEmails,
            count: action.result.asnOrderStatusSummary?.count,
        };
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.asnOrderStatusSummary = data;
        } else if (draft.osnType === "individualJobSiteLevel" && customerId) {
            draft.jobSiteSubscriptionsById[customerId].asnOrderStatusSummary = data;
        }
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/CompleteRemoveSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: string;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.asnOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.accountLevelSubscriptions.asnOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.asnOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count: (draft.accountLevelSubscriptions.asnOrderStatusSummary?.count || 0) - 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary?.count ||
                        0) - 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/CompleteAddSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: SubscribedEmailDto;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.asnOrderStatusSummary?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.accountLevelSubscriptions.asnOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.asnOrderStatusSummary,
                subscribedEmails: emails,
                count: (draft.accountLevelSubscriptions.asnOrderStatusSummary?.count || 0) + 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary
                    ?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary,
                subscribedEmails: emails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary?.count ||
                        0) + 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/CompleteUpdatingSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                customerId: string;
                subscribedEmailDto: SubscribedEmailDto;
            };
        },
    ) => {
        const updatedEmailId = action.result.subscribedEmailDto.email;
        const getUpdatedEmailDtos = (updatedEmailId: string, emailsDto: SubscribedEmailDto[]) => {
            const updatedSubscribedEmails = emailsDto?.map(emailDto => {
                let updatedEmailDto: SubscribedEmailDto = emailDto;
                if (emailDto.email === updatedEmailId) {
                    updatedEmailDto = { ...action.result.subscribedEmailDto };
                }
                return updatedEmailDto;
            });
            return updatedSubscribedEmails;
        };
        if (draft.osnType === "accountLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.accountLevelSubscriptions.asnOrderStatusSummary?.subscribedEmails || [],
            );
            draft.accountLevelSubscriptions.asnOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.asnOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary
                    ?.subscribedEmails || [],
            );
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/CompleteRemovePhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                emailId: string;
            };
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.asnOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.accountLevelSubscriptions.asnOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.asnOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].asnOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/BeginApplyAsnSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = true;
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/ApplyAsnSubscriptionsError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/AsnSubscriptions/CompleteApplyAsnSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
};

export default asnSubscriptions;
